/** Known provider dashboard path prefixes that are safe to deep-link. */
const PROVIDER_PATH_PREFIXES = [
  "/dashboard",
  "/dashboard/audit-trail",
  "/dashboard/activity",
  "/dashboard/portfolios",
  "/dashboard/cases",
  "/dashboard/customers",
  "/dashboard/payments",
  "/dashboard/contact-centre",
  "/dashboard/reports",
  "/dashboard/escalations",
  "/dashboard/team",
  "/dashboard/roles",
  "/dashboard/settings",
];

function pathnameOnly(path) {
  return String(path || "").split("?")[0].split("#")[0];
}

function isAllowedPath(path, prefixes) {
  if (!path || typeof path !== "string") return false;
  const normalized = pathnameOnly(path);
  if (!normalized.startsWith("/dashboard")) return false;
  return prefixes.some(
    (prefix) =>
      normalized === prefix ||
      normalized.startsWith(`${prefix}/`) ||
      (prefix === "/dashboard" && normalized === "/dashboard")
  );
}

function withQuery(basePath, params) {
  const path = pathnameOnly(basePath) || basePath;
  const existing = String(basePath).includes("?")
    ? new URLSearchParams(String(basePath).split("?")[1] || "")
    : new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (!existing.has(key)) existing.set(key, String(value));
  });
  const qs = existing.toString();
  return qs ? `${path}?${qs}` : path;
}

export function enrichActivityPath(rawPath, data = {}) {
  if (!rawPath || typeof rawPath !== "string") return null;
  const path = pathnameOnly(rawPath);
  const caseId = data.case_id || data.caseId || null;
  const customer = data.customer_name || data.customer || null;
  const paymentId = data.payment_id || data.paymentId || null;

  if (path.startsWith("/dashboard/payments")) {
    return withQuery(rawPath, { caseId, customer, paymentId });
  }
  if (path.startsWith("/dashboard/cases")) {
    return withQuery(rawPath, {
      caseId,
      customer,
      debtCustomer: data.debt_customer_slug || null,
    });
  }
  if (path.startsWith("/dashboard/contact-centre")) {
    return withQuery(rawPath, { caseId, customer, tab: data.tab || "calls" });
  }
  if (path.startsWith("/dashboard/customers") && customer) {
    return withQuery(rawPath, { customer });
  }
  return rawPath.includes("?") ? rawPath : path;
}

/**
 * Resolve a clickable path for an activity item on the provider portal.
 * Prefers provider_path when present so ops-only routes are not linked.
 * @returns {string|null}
 */
export function resolveActivityPath(item, { portal = "provider" } = {}) {
  if (!item) return null;
  const data = item.data || {};
  const candidate =
    portal === "provider"
      ? item.provider_path || data.provider_path || item.path || data.path
      : item.path || data.path || item.provider_path || data.provider_path;

  if (!candidate || typeof candidate !== "string") {
    if (data.case_id) {
      const built = enrichActivityPath("/dashboard/cases", data);
      return isAllowedPath(built, PROVIDER_PATH_PREFIXES) ? built : null;
    }
    return null;
  }

  if (!isAllowedPath(candidate, PROVIDER_PATH_PREFIXES)) return null;
  return enrichActivityPath(candidate, data);
}

export function activityActorName(item) {
  const name = String(item?.actor_name || "").trim();
  return name || "System";
}
